import type { DocumentFont } from "@flobi/artwork-core";
import { listLibrary, resolveFont, resolveLibraryFamily, type LibraryFont } from "./fonts";
import { brandKit, fileContext } from "./work";

const same = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * A font asked for by name, wherever this workspace has it, in the order the font picker shows
 * them (Tarek, 31 Oct): the file's brand's own files, then Flobi Fonts, then Google. A name the
 * first two have used to count as missing, since only Google's catalogue was asked.
 */
export function fontFinderOf(from: {
  kit: readonly DocumentFont[];
  library: readonly LibraryFont[];
  fromLibrary?: (family: string, faces: readonly LibraryFont[]) => Promise<DocumentFont>;
  google?: (family: string, weights?: number[], italic?: boolean) => Promise<DocumentFont>;
}) {
  const { kit, library, fromLibrary = resolveLibraryFamily, google = resolveFont } = from;
  return async (family: string, weights: number[], italic: boolean): Promise<DocumentFont> => {
    const brand = kit.find((font) => same(font.family, family));
    if (brand) return brand;
    const faces = library.filter((face) => same(face.family, family));
    // A family with no file a canvas can read (WOFF only) falls through to Google's of that name.
    if (faces.length) {
      const own = await fromLibrary(faces[0]!.family, faces).catch(() => null);
      if (own) return own;
    }
    return google(family, weights, italic);
  };
}

/**
 * The finder for one open file, and its brand's first face to stand in for a font nobody has.
 * What cannot be read is left out: a file in no brand, or Flobi Fonts not answering, still gets Google.
 */
export async function fontFinder(documentId: string): Promise<{ resolve: ReturnType<typeof fontFinderOf>; preferred: string[] }> {
  const [kit, library] = await Promise.all([
    fileContext(documentId).then((context) => (context?.brand?.id ? brandKit(context.brand.id) : null)).catch(() => null),
    listLibrary().catch(() => [] as LibraryFont[]),
  ]);
  const fonts = kit?.fonts ?? [];
  return { resolve: fontFinderOf({ kit: fonts, library }), preferred: fonts.slice(0, 1).map((font) => font.family) };
}
